// CommonInputs.jsx
// 공통 입력 UI 컴포넌트 모음
// - CustomInputBox   : 단일 라인 텍스트 입력
// - CustomTextAreaBox: 멀티라인 텍스트 입력
// - CompactInputCell : 라벨+입력 한 줄 셀 (업체정보 등)
// - InfoCell         : 읽기 전용 정보 표시 셀

// ─── CustomInputBox ────────────────────────────────────────────────────────
/**
 * @param {string}   label       - 라벨 텍스트
 * @param {Component} icon       - 아이콘 컴포넌트 (선택)
 * @param {string}   iconColor   - 아이콘 Tailwind 색상 클래스 (선택)
 * @param {string}   value       - 입력값
 * @param {function} onChange    - 값 변경 콜백 (value: string)
 * @param {string}   placeholder - 플레이스홀더
 * @param {string}   error       - 에러 메시지 (선택)
 */
export function CustomInputBox({
  label,
  icon: IconComponent,
  iconColor,
  value,
  onChange,
  placeholder,
  error,
}) {
  return (
    <div
      className={`border bg-white ${
        error ? 'border-red-200' : 'border-slate-100'
      } focus-within:ring-primary/10 rounded-2xl p-5 shadow-sm transition-all focus-within:ring-2`}
    >
      <label className="mb-2 flex items-center gap-1 text-xs font-bold text-slate-500 uppercase">
        {IconComponent && (
          <IconComponent
            width="16"
            height="16"
            color="currentColor"
            className={iconColor}
          />
        )}
        {label}
      </label>
      <input
        className="w-full border-none bg-transparent p-0 text-base font-extrabold text-slate-900 focus:ring-0"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {error && <p className="mt-2 text-xs font-bold text-red-500">{error}</p>}
    </div>
  );
}

// ─── CustomTextAreaBox ─────────────────────────────────────────────────────
/**
 * @param {string}   label       - 라벨 텍스트
 * @param {Component} icon       - 아이콘 컴포넌트 (선택)
 * @param {string}   iconColor   - 아이콘 Tailwind 색상 클래스 (선택)
 * @param {string}   value       - 입력값
 * @param {function} onChange    - 값 변경 콜백 (value: string)
 * @param {string}   placeholder - 플레이스홀더
 * @param {string}   error       - 에러 메시지 (선택)
 */
export function CustomTextAreaBox({
  label,
  icon: IconComponent,
  iconColor,
  value,
  onChange,
  placeholder,
  rows = '2',
  minHeight = '80px',
  error,
}) {
  return (
    <div
      className={`border bg-white ${
        error ? 'border-red-200' : 'border-slate-100'
      } focus-within:ring-primary/10 rounded-2xl p-5 shadow-sm transition-all focus-within:ring-2`}
    >
      <label className="mb-2 flex items-center gap-1 text-xs font-bold text-slate-500 uppercase">
        {IconComponent && (
          <IconComponent width="16" height="16" className={iconColor} />
        )}
        {label}
      </label>
      <textarea
        rows={rows}
        style={{ minHeight: minHeight }}
        className="w-full resize-none border-none bg-transparent p-0 text-base font-extrabold text-slate-900 focus:ring-0"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {error && <p className="mt-2 text-xs font-bold text-red-500">{error}</p>}
    </div>
  );
}

// ─── CompactInputCell ──────────────────────────────────────────────────────
/**
 * 라벨 + 인라인 입력 한 줄짜리 셀. 업체정보 등 보조 필드에 사용.
 *
 * @param {string}   label    - 좌측 라벨 (최대 4~5자 권장)
 * @param {string}   value    - 입력값
 * @param {function} onChange - 값 변경 콜백 (value: string)
 * @param {string}   type     - input type (기본값 'text')
 */
export function CompactInputCell({ label, value, onChange, type = 'text' }) {
  return (
    <div className="focus-within:border-primary/30 flex w-full items-center gap-2 rounded-xl border border-slate-100 bg-slate-50 p-2 transition-colors">
      <span className="w-18 shrink-0 border-r border-slate-200 text-center text-sm font-bold text-slate-400 uppercase">
        {label}
      </span>
      <input
        className="flex-1 border-none bg-transparent p-1 text-base font-bold text-slate-900 focus:ring-0"
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/**
 * 읽기 전용 정보 표시 셀. 신청자 정보(자동입력) 영역에 사용.
 *
 * @param {string} label - 상단 라벨
 * @param {string} value - 표시할 값
 */
export function InfoCell({ label, value }) {
  return (
    <div className="text-center">
      <span className="text-xs font-bold tracking-tighter text-slate-400 uppercase">
        {label}
      </span>
      <div className="rounded-lg border border-white/50 bg-white/60 py-2 text-xs font-extrabold text-slate-600">
        {value || '-'}
      </div>
    </div>
  );
}
